import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import { notificationPreferenceQueryKeys } from '@/hooks/queries/use-notification-preferences-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  NotificationPreferencesResponse,
  UpdateNotificationPreferencesRequest,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export const useUpdateNotificationPreferencesAction = (brandId: string | null | undefined) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    NotificationPreferencesResponse,
    AxiosError<ApiResponse>,
    UpdateNotificationPreferencesRequest
  >({
    mutationFn: async (payload) => {
      return trackProductAction('notifications', 'update', async () => {
        const response = await api.patch<ApiResponse<NotificationPreferencesResponse>>(
          '/dashboard/users/me/notification-preferences',
          payload
        );

        if (!response.data.data) {
          throw new Error('Notification preferences response is empty');
        }

        return response.data.data;
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(notificationPreferenceQueryKeys.brand(brandId), data);
      queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKeys.brand(brandId) });

      toaster.success({
        title: t('settings.notificationPreferences.successTitle'),
        description: t('settings.notificationPreferences.successDescription'),
      });
    },
    onError: () => {
      toaster.error({
        title: t('common.error'),
        description: t('settings.notificationPreferences.errorDescription'),
      });
    },
  });
};
